<template>
  <button type="button" :aria-pressed="active" :aria-label="label" :title="bar ? undefined : label"
    class="group/mark relative inline-flex items-center justify-center flex-shrink-0 font-semibold transition-all duration-150 active:scale-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50 disabled:opacity-60 disabled:cursor-not-allowed"
    :class="[
      bar ? [small ? 'w-6 h-6 rounded-md' : 'w-9 h-9 rounded-lg', 'border'] : 'w-7 h-7 rounded-md',
      tone,
    ]">
    <!-- Bookmark -->
    <span v-if="kind === 'bookmark'" class="relative inline-flex">
      <!-- Turning it on: the icon springs in, a ring spreads out and sparks fly off it -->
      <template v-if="burst">
        <span class="mark-ring absolute -inset-1 rounded-full border-2 border-blue-500 pointer-events-none" aria-hidden="true"></span>
        <span v-for="(spark, index) in SPARKS" :key="index" class="mark-spark absolute left-1/2 top-1/2 w-1 h-1 rounded-full pointer-events-none"
          :class="spark" :style="{ '--angle': `${index * 45}deg`, animationDelay: `${(index % 2) * 40}ms` }" aria-hidden="true"></span>
      </template>
      <svg v-if="active" :class="[iconSize, burst ? 'mark-pop' : '']" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M6.32 2.577a49.255 49.255 0 0 1 11.36 0c1.497.174 2.57 1.46 2.57 2.93V21a.75.75 0 0 1-1.085.67L12 18.089l-7.165 3.583A.75.75 0 0 1 3.75 21V5.507c0-1.47 1.073-2.756 2.57-2.93Z"/>
      </svg>
      <svg v-else :class="iconSize" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0 1 11.186 0Z"/>
      </svg>
    </span>

    <!-- Ignore, or bring back an ignored job (the eye) -->
    <template v-else>
      <svg v-if="active" :class="iconSize" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"/>
        <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"/>
      </svg>
      <svg v-else :class="iconSize" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88"/>
      </svg>
    </template>

    <!-- In the action bar the name is a tooltip, like the other icon buttons -->
    <span v-if="bar" role="tooltip"
      class="pointer-events-none absolute top-full left-1/2 -translate-x-1/2 z-40 mt-1.5 px-2 py-1 rounded-md bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-xs font-medium whitespace-nowrap shadow-lg opacity-0 transition-opacity group-hover/mark:opacity-100 group-hover/mark:delay-300 group-focus-visible/mark:opacity-100">{{ label }}</span>
  </button>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue'

// One control for both marks. `active` is whether the job has the mark now. The compact form
// (icons on a list row) says what a click does in the browser's own tooltip; the bar form (the
// job's action bar) is a bordered icon with the same tooltip as the other icon buttons.
const props = defineProps({
  kind: { type: String, required: true, validator: (value) => ['bookmark', 'ignore'].includes(value) },
  active: { type: Boolean, default: false },
  bar: { type: Boolean, default: false },
  small: { type: Boolean, default: false }, // the bar form at the size of a line of text
})

const iconSize = computed(() => (props.small ? 'w-3.5 h-3.5' : 'w-4 h-4'))

const WORDS = {
  bookmark: { off: 'Bookmark', on: 'Bookmarked', action: { off: 'Bookmark this job', on: 'Remove bookmark' } },
  ignore: { off: 'Ignore', on: 'Ignored', action: { off: 'Ignore this job', on: 'Restore this job' } },
}

// The burst plays when a bookmark is switched on while the button is on screen. A job that
// arrives already bookmarked (a reload, another view) does not play it.
const SPARKS = ['bg-blue-500', 'bg-amber-400', 'bg-sky-400', 'bg-amber-400', 'bg-blue-500', 'bg-amber-400', 'bg-sky-400', 'bg-amber-400']
const BURST_MS = 750
const burst = ref(false)
let timer = null

watch(() => props.active, (now, was) => {
  if (props.kind !== 'bookmark' || !now || was) return

  burst.value = true
  clearTimeout(timer)
  timer = setTimeout(() => { burst.value = false }, BURST_MS)
})
onBeforeUnmount(() => clearTimeout(timer))

const state = computed(() => (props.active ? 'on' : 'off'))
const label = computed(() => WORDS[props.kind].action[state.value])

const tone = computed(() => {
  if (props.active && props.kind === 'bookmark') {
    return props.bar
      ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/30'
      : 'text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30'
  }
  if (props.active) {
    return props.bar
      ? 'bg-gray-100 dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700'
      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
  }
  return props.bar
    ? 'bg-white dark:bg-[#1C2128] border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'
    : 'text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
})
</script>

<style scoped>
/* Keyframes cannot be written as utilities, so they live here. */
.mark-pop { animation: mark-pop 520ms cubic-bezier(0.34, 1.56, 0.64, 1) both; }
.mark-ring { animation: mark-ring 600ms ease-out both; }
.mark-spark { animation: mark-spark 650ms cubic-bezier(0.2, 0.8, 0.3, 1) both; }

@keyframes mark-pop {
  0% { transform: scale(0.4) rotate(-14deg); }
  45% { transform: scale(1.4) rotate(8deg); }
  75% { transform: scale(0.92) rotate(-3deg); }
  100% { transform: scale(1) rotate(0); }
}
@keyframes mark-ring {
  0% { transform: scale(0.3); opacity: 0.8; }
  100% { transform: scale(1.8); opacity: 0; }
}
@keyframes mark-spark {
  0% { transform: translate(-50%, -50%) rotate(var(--angle)) translateY(-4px) scale(0.5); opacity: 1; }
  100% { transform: translate(-50%, -50%) rotate(var(--angle)) translateY(-19px) scale(1); opacity: 0; }
}

@media (prefers-reduced-motion: reduce) {
  .mark-pop, .mark-ring, .mark-spark { animation: none; }
  .mark-ring, .mark-spark { display: none; }
}
</style>
